import { useEffect, useMemo, useState } from "react";
import { useApi } from "@/lib/api";
import {
  T,
  sans,
  mono,
  AGENT_COLOR,
  TicketModal,
  type AgentRow,
  type TicketRow,
  type Memory,
  type ProjectRow,
} from "../operator/OperatorPage";

type StreamItem =
  | {
      kind: "memory";
      id: string;
      agent: string | null;
      title: string;
      body: string;
      ts: string;
    }
  | {
      kind: "ticket_update";
      id: string;
      agent: string | null;
      slug: string;
      title: string;
      status: TicketRow["status"];
      logTail: string | null;
      ts: string;
    };

function dayBucket(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const dayDiff = Math.floor(
    (today.setHours(0, 0, 0, 0) - new Date(d).setHours(0, 0, 0, 0)) /
      86_400_000,
  );
  if (dayDiff === 0) return "Today";
  if (dayDiff === 1) return "Yesterday";
  if (dayDiff < 7) return `${dayDiff} days ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function timeStamp(iso: string): string {
  return new Date(iso).toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

const STATUS_TONE: Record<string, { bg: string; color: string; label: string }> = {
  todo: { bg: T.panel2, color: T.ink2, label: "To do" },
  in_progress: { bg: T.skySoft, color: T.skyDark, label: "Live" },
  review: { bg: T.amberSoft, color: T.amber, label: "Review" },
  done: { bg: T.greenSoft, color: T.green, label: "Done" },
  archived: { bg: T.line, color: T.ink3, label: "Archived" },
};

function StreamCard({
  item,
  onOpenTicket,
}: {
  item: StreamItem;
  onOpenTicket: (slug: string) => void;
}) {
  const color = item.agent ? AGENT_COLOR[item.agent] ?? T.skyDark : T.ink3;

  if (item.kind === "memory") {
    return (
      <div
        style={{
          display: "flex",
          gap: 10,
          padding: "10px 0",
          alignItems: "flex-start",
        }}
      >
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: "50%",
            background: `${color}22`,
            color,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: sans,
            fontSize: 11,
            fontWeight: 700,
            border: `1.5px solid ${color}55`,
            flexShrink: 0,
          }}
        >
          {(item.agent ?? "?").charAt(0).toUpperCase()}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              gap: 8,
              marginBottom: 3,
              flexWrap: "wrap",
            }}
          >
            <span
              style={{
                fontFamily: sans,
                fontSize: 12,
                fontWeight: 700,
                color,
                textTransform: "lowercase",
              }}
            >
              {item.agent ?? "system"}
            </span>
            <span
              style={{
                fontFamily: sans,
                fontSize: 11,
                color: T.ink2,
                fontWeight: 600,
              }}
            >
              wrote a session log
            </span>
            <span
              style={{ fontFamily: mono, fontSize: 10, color: T.ink3 }}
            >
              {timeStamp(item.ts)}
            </span>
          </div>
          <div
            style={{
              fontFamily: sans,
              fontSize: 13,
              fontWeight: 600,
              color: T.ink,
              lineHeight: 1.4,
              marginBottom: 4,
            }}
          >
            {item.title}
          </div>
          <div
            style={{
              fontFamily: sans,
              fontSize: 12,
              color: T.ink2,
              lineHeight: 1.55,
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              background: T.panel2,
              padding: "8px 12px",
              borderRadius: 6,
              borderLeft: `2px solid ${color}55`,
            }}
          >
            {item.body}
          </div>
        </div>
      </div>
    );
  }

  const tone = STATUS_TONE[item.status];
  return (
    <div
      onClick={() => onOpenTicket(item.slug)}
      style={{
        display: "flex",
        gap: 10,
        padding: "10px 0",
        alignItems: "flex-start",
        cursor: "pointer",
      }}
    >
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: "50%",
          background: `${color}22`,
          color,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: sans,
          fontSize: 11,
          fontWeight: 700,
          border: `1.5px solid ${color}55`,
          flexShrink: 0,
        }}
      >
        {(item.agent ?? "?").charAt(0).toUpperCase()}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 3,
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              fontFamily: sans,
              fontSize: 12,
              fontWeight: 700,
              color,
              textTransform: "lowercase",
            }}
          >
            {item.agent ?? "system"}
          </span>
          <span
            style={{ fontFamily: sans, fontSize: 11, color: T.ink2 }}
          >
            updated
          </span>
          <code
            style={{
              fontFamily: mono,
              fontSize: 10,
              color: T.skyDark,
              background: T.skySoft,
              padding: "1px 6px",
              borderRadius: 3,
              fontWeight: 700,
            }}
          >
            {item.slug}
          </code>
          <span
            style={{
              background: tone.bg,
              color: tone.color,
              padding: "2px 7px",
              borderRadius: 999,
              fontFamily: sans,
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}
          >
            {tone.label}
          </span>
          <span style={{ fontFamily: mono, fontSize: 10, color: T.ink3 }}>
            {timeStamp(item.ts)}
          </span>
        </div>
        <div
          style={{
            fontFamily: sans,
            fontSize: 13,
            fontWeight: 600,
            color: T.ink,
            lineHeight: 1.4,
            marginBottom: item.logTail ? 4 : 0,
          }}
        >
          {item.title}
        </div>
        {item.logTail && (
          <div
            style={{
              fontFamily: sans,
              fontSize: 11.5,
              color: T.ink2,
              fontStyle: "italic",
              lineHeight: 1.5,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {item.logTail}
          </div>
        )}
      </div>
    </div>
  );
}

function NowCard({
  ticket,
  onOpen,
}: {
  ticket: TicketRow;
  onOpen: (slug: string) => void;
}) {
  const color = ticket.agent ? AGENT_COLOR[ticket.agent] ?? T.skyDark : T.skyDark;
  const tail = ticket.log
    ? ticket.log
        .split("\n")
        .filter((l) => l.trim())
        .slice(-2)
    : [];

  return (
    <div
      onClick={() => onOpen(ticket.slug)}
      style={{
        background: T.panel,
        border: `1px solid ${T.line}`,
        borderLeft: `3px solid ${color}`,
        borderRadius: 8,
        padding: "12px 14px",
        cursor: "pointer",
        transition: "transform 0.15s ease, box-shadow 0.15s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-1px)";
        e.currentTarget.style.boxShadow = "0 4px 12px rgba(28,25,23,0.08)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 6,
          flexWrap: "wrap",
        }}
      >
        <span
          className="animate-pulse"
          style={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            background: T.green,
            display: "inline-block",
            flexShrink: 0,
          }}
        />
        {ticket.agent && (
          <span
            style={{
              color,
              fontFamily: sans,
              fontSize: 11,
              fontWeight: 700,
              textTransform: "lowercase",
            }}
          >
            @{ticket.agent}
          </span>
        )}
        <code style={{ fontFamily: mono, fontSize: 9.5, color: T.ink3 }}>
          {ticket.slug}
        </code>
        {ticket.current_step && (
          <span
            style={{
              fontFamily: mono,
              fontSize: 9.5,
              color: T.skyDark,
              fontWeight: 600,
            }}
          >
            {ticket.current_step}
          </span>
        )}
      </div>
      <div
        style={{
          fontFamily: sans,
          fontSize: 12.5,
          fontWeight: 600,
          color: T.ink,
          lineHeight: 1.35,
          marginBottom: tail.length > 0 ? 6 : 0,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {ticket.title}
      </div>
      {tail.map((line, i) => (
        <div
          key={i}
          style={{
            fontFamily: mono,
            fontSize: 10,
            color: T.ink3,
            lineHeight: 1.5,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            paddingLeft: 8,
            borderLeft: `2px solid ${T.line}`,
            marginBottom: i < tail.length - 1 ? 2 : 0,
          }}
          title={line}
        >
          {line}
        </div>
      ))}
    </div>
  );
}

export function AgentStreamView() {
  const api = useApi();
  const [agents, setAgents] = useState<AgentRow[]>([]);
  const [tickets, setTickets] = useState<TicketRow[]>([]);
  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [memories, setMemories] = useState<Memory[]>([]);
  const [openSlug, setOpenSlug] = useState<string | null>(null);
  const [agentFilter, setAgentFilter] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function fetchAll() {
      try {
        const [aRes, tRes, pRes, mRes] = await Promise.all([
          api("/api/agents"),
          api("/api/tickets"),
          api("/api/projects"),
          api("/api/memories"),
        ]);
        if (cancelled) return;
        if (aRes.ok) setAgents((await aRes.json()) as AgentRow[]);
        if (tRes.ok) setTickets((await tRes.json()) as TicketRow[]);
        if (pRes.ok) setProjects((await pRes.json()) as ProjectRow[]);
        if (mRes.ok) setMemories((await mRes.json()) as Memory[]);
      } catch {
        // ignore
      }
    }
    void fetchAll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stream = useMemo<StreamItem[]>(() => {
    const items: StreamItem[] = [];
    for (const m of memories) {
      if (agentFilter && m.agent !== agentFilter) continue;
      items.push({
        kind: "memory",
        id: m.id,
        agent: m.agent,
        title: m.title ?? m.body.split("\n")[0].slice(0, 80),
        body: m.body,
        ts: m.created_at,
      });
    }
    for (const t of tickets) {
      if (agentFilter && t.agent !== agentFilter) continue;
      const ageMs = Date.now() - new Date(t.updated_at).getTime();
      if (ageMs > 7 * 86_400_000) continue;
      const tail = t.log
        ? t.log
            .split("\n")
            .map((l) => l.trim())
            .filter(Boolean)
            .slice(-1)[0]
        : null;
      items.push({
        kind: "ticket_update",
        id: t.id,
        agent: t.agent,
        slug: t.slug,
        title: t.title,
        status: t.status,
        logTail: tail ?? null,
        ts: t.updated_at,
      });
    }
    return items
      .sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime())
      .slice(0, 60);
  }, [memories, tickets, agentFilter]);

  const grouped = useMemo(() => {
    const map = new Map<string, StreamItem[]>();
    for (const item of stream) {
      const bucket = dayBucket(item.ts);
      const list = map.get(bucket) ?? [];
      list.push(item);
      map.set(bucket, list);
    }
    return Array.from(map.entries());
  }, [stream]);

  const inProgress = tickets
    .filter((t) => t.status === "in_progress")
    .filter((t) => !agentFilter || t.agent === agentFilter)
    .sort(
      (a, b) =>
        new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
    )
    .slice(0, 10);

  const activeProject = projects
    .filter((p) => p.status === "active")
    .sort((a, b) => b.ticket_count - a.ticket_count)[0];

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      <div
        style={{
          display: "flex",
          gap: 6,
          alignItems: "center",
          flexWrap: "wrap",
          flexShrink: 0,
        }}
      >
        <span
          style={{
            fontFamily: sans,
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: T.ink3,
            marginRight: 4,
          }}
        >
          Filter
        </span>
        <button
          onClick={() => setAgentFilter(null)}
          style={{
            background: agentFilter === null ? T.skyDark : T.panel,
            color: agentFilter === null ? "#fff" : T.ink2,
            border: `1px solid ${agentFilter === null ? T.skyDark : T.line}`,
            padding: "4px 10px",
            borderRadius: 999,
            fontFamily: sans,
            fontSize: 11,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          All
        </button>
        {agents.map((a) => {
          const color = AGENT_COLOR[a.name] ?? T.skyDark;
          const active = agentFilter === a.name;
          return (
            <button
              key={a.name}
              onClick={() => setAgentFilter(a.name)}
              style={{
                background: active ? color : T.panel,
                color: active ? "#fff" : color,
                border: `1px solid ${active ? color : T.line}`,
                padding: "4px 10px",
                borderRadius: 999,
                fontFamily: sans,
                fontSize: 11,
                fontWeight: 600,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              {a.current_ticket_status === "in_progress" && (
                <span
                  className="animate-pulse"
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: active ? "#fff" : T.green,
                  }}
                />
              )}
              {a.display_name}
            </button>
          );
        })}
        {activeProject && (
          <span
            style={{
              marginLeft: "auto",
              fontFamily: sans,
              fontSize: 11,
              color: T.ink2,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <span style={{ color: T.ink3 }}>most active project:</span>
            <span style={{ fontWeight: 600, color: T.ink }}>
              {activeProject.name}
            </span>
            <span style={{ color: T.ink3, fontFamily: mono, fontSize: 10 }}>
              {activeProject.ticket_count} tickets
            </span>
          </span>
        )}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr)",
          gap: 12,
          flex: 1,
          minHeight: 0,
        }}
      >
        <div
          style={{
            background: T.panel,
            border: `1px solid ${T.line}`,
            borderRadius: 8,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          <div
            style={{
              padding: "11px 16px",
              borderBottom: `1px solid ${T.line}`,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontFamily: sans,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.13em",
              textTransform: "uppercase",
              color: T.ink2,
            }}
          >
            <span>Activity stream</span>
            <span style={{ color: T.green, fontWeight: 700 }}>● live</span>
          </div>
          <div
            style={{
              flex: 1,
              overflowY: "auto",
              padding: "4px 18px 14px",
            }}
          >
            {grouped.length === 0 ? (
              <div
                style={{
                  padding: "30px 0",
                  fontFamily: sans,
                  fontSize: 12,
                  color: T.ink3,
                  textAlign: "center",
                  fontStyle: "italic",
                }}
              >
                {stream.length === 0 && memories.length + tickets.length > 0
                  ? "No activity matches this filter."
                  : "Loading…"}
              </div>
            ) : (
              grouped.map(([bucket, items]) => (
                <div key={bucket}>
                  <div
                    style={{
                      position: "sticky",
                      top: 0,
                      background: T.panel,
                      padding: "10px 0 6px",
                      fontFamily: sans,
                      fontSize: 9,
                      fontWeight: 700,
                      letterSpacing: "0.13em",
                      textTransform: "uppercase",
                      color: T.skyDark,
                      borderBottom: `1px solid ${T.line}`,
                      zIndex: 1,
                    }}
                  >
                    {bucket}
                  </div>
                  {items.map((item) => (
                    <StreamCard
                      key={`${item.kind}-${item.id}-${item.ts}`}
                      item={item}
                      onOpenTicket={setOpenSlug}
                    />
                  ))}
                </div>
              ))
            )}
          </div>
        </div>

        <div
          style={{
            background: T.panel,
            border: `1px solid ${T.line}`,
            borderRadius: 8,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          <div
            style={{
              padding: "11px 14px",
              borderBottom: `1px solid ${T.line}`,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontFamily: sans,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.13em",
              textTransform: "uppercase",
              color: T.ink2,
            }}
          >
            <span>In progress · now</span>
            <span style={{ color: T.ink3, fontWeight: 600 }}>
              {inProgress.length}
            </span>
          </div>
          <div
            style={{
              flex: 1,
              overflowY: "auto",
              padding: 12,
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            {inProgress.length === 0 ? (
              <div
                style={{
                  padding: "30px 8px",
                  fontFamily: sans,
                  fontSize: 12,
                  color: T.ink3,
                  textAlign: "center",
                  fontStyle: "italic",
                }}
              >
                Nothing in progress.
              </div>
            ) : (
              inProgress.map((t) => (
                <NowCard key={t.id} ticket={t} onOpen={setOpenSlug} />
              ))
            )}
          </div>
        </div>
      </div>

      {openSlug && (
        <TicketModal slug={openSlug} onClose={() => setOpenSlug(null)} />
      )}
    </div>
  );
}
